import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AccountCircle } from "@mui/icons-material";
import {
  AppBar,
  CssBaseline,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Divider,
  Hidden,
  Drawer,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CheckIcon from "@mui/icons-material/Check";
import LanguageOutlined from "@mui/icons-material/Language";
import { Link } from "react-router-dom";
import "./Admin-nav.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import common from "../../common/common";
import { logout } from "../../slices/auth";
import { toggleShowAllVideos, setShowAllVideos } from "../../slices/dashboard-carrousel";

import appLogo from "../../img/2025_GetuppLogo_full_gold.png";
import en from "../../img/flags/en.png";
import da from "../../img/flags/da.png";

function AdminNav() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorel, setAnchorel] = useState(null);
  const isMenuOpen = Boolean(anchorel);
  const [openDrawer, setDrawerOpen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const showAllVideos = useSelector((state) => state.dashboardCarrouselVideos.showAllVideos);
  const isHoverDevice = window.matchMedia('(hover: hover)').matches;

  useEffect(() => {
    if (localStorage.getItem("show_all_videos") === null) {
      dispatch(setShowAllVideos(true));
    }
  }, []);

  function handleClick(event) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleClose() {
    setAnchorEl(null);
  }

  const handleProfileMenuChange = (value) => {
    setAnchorEl(null);
  };

  const handleLanguageMenuOpen = (event) => {
    setAnchorel(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorel(null);
  };

  const handleLanguageChange = (value) => {
    i18n.changeLanguage(value);
    localStorage.setItem("language_key", value);
    setAnchorel(null);
  };

  const handleDrawerToggle = () => {
    let open = openDrawer;
    setDrawerOpen(!open);
  };

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    common.on("logout", () => {
      logOut();
    });

    return () => {
      common.remove("logout");
    };
  }, [logOut]);

  return (
    <div>
      <AppBar position="sticky" className="navbar">
        <CssBaseline />
        <Toolbar>
          <Link to="/" className="align-items-center">
            <img className="navLogo" src={appLogo} alt="" />
          </Link>

          {/* Desktop nav links */}
          <Hidden smDown>
            <div className="navList">
              <Link
                to="/all-videos"
                className={`navItems ${location.pathname === "/all-videos" ? "active" : ""}`}
              >
                {t("All Videos")}
              </Link>
              <Link
                to="/sessions"
                className={`navItems ${location.pathname === "/sessions" ? "active" : ""}`}
              >
                {t("Training Sessions")}
              </Link>
            </div>
          </Hidden>

          {/* Right side items */}
          <div className="admin-nav-right">
            {/* Admin label */}
            <span className="admin-label">Admin</span>

            {/* Globe / language switcher - always visible */}
            <IconButton
              aria-label="language selected by current user"
              aria-controls="change-language-menu"
              aria-haspopup="true"
              onClick={handleLanguageMenuOpen}
              color="inherit"
              size="small"
            >
              <LanguageOutlined />
            </IconButton>
            <Menu
              anchorel={anchorel}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              id="change-language-menu"
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={isMenuOpen}
              onClose={handleLanguageMenuClose}
              style={{ marginTop: '2.2em' }}
            >
              <MenuItem onClick={() => handleLanguageChange('en')} value="en"><img className='flag-icon' src={en} alt=""/> English {i18n.language === 'en' && <CheckIcon className='selected-language'/>}</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('da')} value="da"><img className='flag-icon' src={da} alt=""/> Danish {i18n.language === 'da' && <CheckIcon className='selected-language'/>}</MenuItem>
              <Divider />
              <MenuItem onClick={() => dispatch(toggleShowAllVideos())} dense>
                <FormControlLabel
                  control={<Checkbox checked={showAllVideos} size="small" />}
                  label={t("Show all videos")}
                  onClick={(e) => e.preventDefault()}
                />
              </MenuItem>
            </Menu>

            {/* Profile dropdown - all screen sizes */}
              <Button
                aria-owns={anchorEl ? "profile-menu" : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                onMouseOver={isHoverDevice ? handleClick : undefined}
                aria-label="profile selected by current user"
                aria-controls="profile-menu"
                className="admin-profile-btn"
                size="small"
              >
                <AccountCircle style={{ fontSize: "25px" }} className="admin-nav-avatar" />
              </Button>
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={isHoverDevice ? { onMouseLeave: handleClose } : undefined}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                keepMounted
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                style={{ marginTop: "2.2em" }}
              >
                <MenuItem onClick={() => handleProfileMenuChange()}>
                  <Link className="profile-links" to="/add-category">{t("Add Category")}</Link>
                </MenuItem>
                <MenuItem onClick={() => handleProfileMenuChange()}>
                  <Link className="profile-links" to="/tags">{t("Tags")}</Link>
                </MenuItem>
                <MenuItem onClick={() => handleProfileMenuChange()}>
                  <Link className={`profile-links ${location.pathname === "/upload" ? "active" : ""}`} to="/upload">{t("Upload Video")}</Link>
                </MenuItem>
                <MenuItem onClick={() => handleProfileMenuChange()}>
                  <Link className={`profile-links ${location.pathname === "/upload-session" ? "active" : ""}`} to="/upload-session">{t("Upload Session")}</Link>
                </MenuItem>
                <MenuItem onClick={() => handleProfileMenuChange()}>
                  <Link className={`profile-links ${location.pathname === "/upload-live-event" ? "active" : ""}`} to="/upload-live-event">{t("Upload Live Event")}</Link>
                </MenuItem>
                <Divider />
                <MenuItem onClick={logOut}>{t("Logout")}</MenuItem>
              </Menu>

            {/* Hamburger - mobile only */}
            <Hidden smUp>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={handleDrawerToggle}
                size="small"
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
          </div>

          {/* Mobile drawer */}
          <Hidden smUp>
            <Drawer
              variant="temporary"
              anchor={"right"}
              open={openDrawer}
              onClose={handleDrawerToggle}
              className="mobile-drawer"
            >
              <Link onClick={handleDrawerToggle} to="/all-videos" className={`navItems ${location.pathname === "/all-videos" ? "active" : ""}`}>{t("All Videos")}</Link>
              <Link onClick={handleDrawerToggle} to="/sessions" className={`navItems ${location.pathname === "/sessions" ? "active" : ""}`}>{t("Training Sessions")}</Link>
              <Link onClick={handleDrawerToggle} to="/upload" className={`navItems ${location.pathname === "/upload" ? "active" : ""}`}>{t("Upload Video")}</Link>
              <Link onClick={handleDrawerToggle} to="/upload-session" className={`navItems ${location.pathname === "/upload-session" ? "active" : ""}`}>{t("Upload Session")}</Link>
              <Link onClick={handleDrawerToggle} to="/upload-live-event" className={`navItems ${location.pathname === "/upload-live-event" ? "active" : ""}`}>{t("Upload Live Event")}</Link>
              <Divider />
              <MenuItem onClick={() => handleLanguageChange('en')} value="en">
                <img className='flag-icon' src={en} alt=""/> English {i18n.language === 'en' && <CheckIcon className='selected-language'/>}
              </MenuItem>
              <MenuItem onClick={() => handleLanguageChange('da')} value="da">
                <img className='flag-icon' src={da} alt=""/> Danish {i18n.language === 'da' && <CheckIcon className='selected-language'/>}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => dispatch(toggleShowAllVideos())} dense>
                <FormControlLabel
                  control={<Checkbox checked={showAllVideos} size="small" />}
                  label={t("Show all videos")}
                  onClick={(e) => e.preventDefault()}
                />
              </MenuItem>
              <Divider />
              <MenuItem onClick={logOut}>{t("Logout")}</MenuItem>
            </Drawer>
          </Hidden>

        </Toolbar>
      </AppBar>
    </div>
  );
}

export default AdminNav;
